import { Button, Input, Typography, List, Form } from "antd";
import styled from "styled-components";
import {
  BLACK,
  DELETE_GREY,
  GRAY_100,
  GRAY_400,
  GRAY_50,
  GRAY_500,
  GRAY_700,
  GRAY_800,
  PRIMARY_400,
  PRIMARY_50,
  PRIMARY_600,
  PRIMARY_800,
  WHITE,
  ERROR_600
} from "../../../constant/colors";
import { fontWeights, sizes } from "../../../constant/styles";
import { font14pxR } from "../../Typography";

const { TextArea } = Input;
const { Text } = Typography;

export const NOTE_CHARACTER_LIMIT = 1000;

export const StyledTextArea = styled(TextArea)`
  .ant-input {
    ${font14pxR}
    color: ${GRAY_700};
    padding-right: 25px;
  }
`;

export const StyledNoteCount = styled.div`
  ${font14pxR}
  color: ${BLACK};
  opacity: 0.25;
  float: right;
  margin-top: 5px;
`;

export const TextAreaStyled = styled(TextArea)`
  padding: 0px 16px 0px 0px;
  width: 100%;

  &.ant-input-textarea-show-count::after {
    display: none;
  }

  textarea {
    min-height: 88px !important;
    max-height: 230px !important;
  }
`;

export const FormItemStyled = styled(Form.Item)`
  margin-left: 16px;
  margin-bottom: 0;

  .ant-form-item-explain-error {
    display: none;
  }
`;

export const ValidationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 16px;
  gap: 10px;
  color: ${ERROR_600};
  font-size: ${sizes.medium};
  font-weight: ${({ hasError }) => (hasError ? fontWeights.bold : fontWeights.medium)};
`;

export const CharacterCount = styled.span`
  color: ${({ hasError }) => (hasError ? ERROR_600 : BLACK)};
  opacity: ${({ hasError }) => (hasError ? 1 : 0.25)};
  font-size: ${sizes.medium};
  font-weight: ${({ hasError }) => (hasError ? fontWeights.bold : fontWeights.medium)};
`;

export const EditContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  background: ${WHITE};
  border-radius: 5px;
`;

export const EditNoteTextArea = styled(TextArea)`
  width: 100%;
  height: 100%;
  background: ${WHITE};

  &.ant-input-textarea-show-count::after {
    display: none;
  }

  textarea {
    border: 1px solid ${GRAY_400};
    border-radius: 5px;
    width: 100%;
    min-height: 146px;
    max-height: ${({ hasError }) => (hasError ? "190px" : "146px")};
    height: ${({ hasError }) => (hasError ? "190px" : "146px")} !important;
    resize: none;
  }
`;

export const NoteList = styled(List)`
  width: 100%;
  overflow-y: auto;
  padding: 0px 16px;
  height: 100%;

  .ant-list-item {
    padding: 4px 0px;
    transition: all 0.3s ease;

    &:hover {
      .note-action-menu {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  &.ant-list-split .ant-list-item {
    border-bottom: 0px;
  }

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 15px;
    background-color: ${WHITE};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
    border: 4px solid ${WHITE};
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

export const NoteItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: ${({ isEditing }) => (isEditing ? "transparent" : GRAY_100)};
  padding: ${({ isEditing }) => (isEditing ? "0px" : "10px")};
  border-radius: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ isEditing }) => (isEditing ? "transparent" : PRIMARY_50)};
    box-shadow: ${({ isEditing }) => (isEditing ? "none" : "0px 2px 4px rgba(0, 0, 0, 0.05)")};
  }
`;

export const ActionMenuWrapper = styled.div`
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};
  align-self: center;
  transition: all 0.2s ease;
  cursor: pointer;
`;

export const CreatedByLabel = styled(Text)`
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.small};
  color: ${GRAY_500};
  cursor: pointer;
`;

export const NoteContent = styled(Text)`
  font-weight: ${fontWeights.regular};
  color: ${BLACK};
  font-size: ${sizes.medium};
  white-space: pre-line;
`;

export const ButtonContainerAddNote = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 6px 16px 0px 0px;
`;

export const AddNoteButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;

  color: ${WHITE};
  border-radius: 4px;
  background: ${PRIMARY_600};
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
  border: 1px solid ${PRIMARY_600};
  min-width: ${({ isEditing }) => (isEditing ? "0px" : "100px")};
  font-size: ${({ isEditing }) => (isEditing ? sizes.small : sizes.medium)};

  :disabled,
  :hover:disabled {
    background: ${PRIMARY_600};
    opacity: 0.3;
    border: 1px solid ${PRIMARY_600};
    box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
    border-radius: 4px;
    color: ${WHITE};
  }

  :hover {
    background: ${PRIMARY_800};
    border: 1px solid ${PRIMARY_600};
  }

  :active {
    background: ${PRIMARY_600};
    border: 4px solid ${PRIMARY_400};
    border-radius: 4px;
    color: ${WHITE};
    padding-top: 1px;
  }
  :focus {
    background: ${PRIMARY_600};
    color: ${WHITE};
  }
`;

export const CancelNoteButton = styled(Button)`
  color: ${GRAY_500};
  font-weight: ${fontWeights.semibold};
  display: flex;
  justify-content: center;
  align-items: center;

  :disabled,
  :hover:disabled {
    opacity: 0.3;
    color: ${GRAY_500};
  }

  :hover {
    background: ${GRAY_50};
    color: ${GRAY_500};
    border-radius: 4px;
  }

  :active,
  :focus {
    color: ${GRAY_500};
  }
`;

export const StyledEmptyText = styled(Text)`
  color: rgba(0, 0, 0, 0.4);
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.medium};
`;

export const HeaderContainer = styled.div`
  background-color: ${GRAY_50};
  padding: 12px 16px;
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.medium};
  color: ${GRAY_800};
  border-bottom: 1px solid ${DELETE_GREY};
`;

export const NotesContainer = styled.div`
  height: 0;
  border: 1px solid ${DELETE_GREY};
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 500px;
`;

export const InputAreaContainer = styled.div`
  padding: 16px 0px;
`;

export const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: space-between;
`;

export const CancelEditNoteButton = styled(Button)`
  display: flex;
  justify-content: center;
  color: ${GRAY_700};
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.small};
  border-bottom: 1px solid ${GRAY_700};
  border-radius: 0px;

  :disabled,
  :hover:disabled {
    opacity: 0.3;
    color: ${GRAY_700};
    border-bottom: 1px solid ${GRAY_700};
    border-radius: 0px;
  }

  :hover {
    background: ${GRAY_50};
    color: ${GRAY_700};
    border-bottom: 1px solid ${GRAY_700};
    border-radius: 0px;
  }

  :active,
  :focus {
    color: ${GRAY_700};
    border-bottom: 1px solid ${GRAY_700};
    border-radius: 0px;
  }
`;

export const FormItemStyledEditNote = styled(Form.Item)`
  margin: 0;
  width: 100%;

  .ant-form-item-explain-error {
    display: none;
  }
`;

export const EditNoteErrorContainer = styled.div`
  color: ${ERROR_600};
  white-space: pre-line;
  font-size: ${sizes.medium};
  font-weight: ${({ hasError }) => (hasError ? fontWeights.bold : fontWeights.medium)};
`;

//Shared styled components for Edit Notes Action Menu UI
export const StyledThreeDotsButton = styled(Button)`
  border-radius: 15px;
  padding: 5px;
  width: 30px;
  height: 30px;
  background: ${PRIMARY_600};
  span {
    background-color: ${WHITE};
  }

  :active,
  :focus,
  :hover {
    background: ${PRIMARY_600};
    span {
      background-color: ${WHITE};
    }
  }
`;

export const StyledMenuItemButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px;
  color: ${BLACK};
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  background: transparent;
  border: none;

  &:hover,
  &:focus {
    background-color: transparent;
    border: none;
  }
`;
